import React from 'react';
import { useEffect, useState } from 'react';
import {
  type StoreCarouselData,
  StoreCarouselImageType,
} from '../../Api/apis/types/carousel.types';
import { GetStoreCarouselList, GetStoreCarouselListForStorePublic } from '../../Api/apis/carouselApi';
import { STORAGE_CONSTANTS } from '../../shared/Constants/Constants';
import BrandBanner from '../../assets/images/title-bg.png';
import ImageCarousel, { type ImageCarouselItem } from './ImageCarousel';
import { getImageUrl } from '../../Utils/imageUtils';


export const getCarouselImageUrl = (data: StoreCarouselData): string => {
  if (data.carousel_image_type === StoreCarouselImageType.CAROUSEL_IMAGE) {
    return getImageUrl(data.image);
  }
  return getImageUrl(data.product.product_image);
};

const getImageCarouselItems = (
  data: StoreCarouselData[]
): ImageCarouselItem[] => {
  return data.map((item) => ({
    src: getCarouselImageUrl(item),
    alt: item.title || '',
  }));
};

interface StoreCarouselProps {
  /** Store whose banners to show; defaults to the logged-in seller's store */
  storeId?: string | number;
  /** Drawn over the brand artwork when the store has no active banners */
  fallbackContent?: React.ReactNode;
}

/**
 * Active banners of a store, in their saved order.
 * Falls back to the Sajilo Life artwork (plus `fallbackContent`) when there is
 * no store id, the store has no active banners, or the request fails, so it is
 * safe on public pages.
 */
export default function StoreCarousel({
  storeId,
  fallbackContent,
}: StoreCarouselProps) {
  const id =
    storeId != null && storeId !== ''
      ? String(storeId)
      : localStorage.getItem(STORAGE_CONSTANTS.STORE_ID);

  const [carouselData, setCarouselData] = useState<StoreCarouselData[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchCarouselData = async (id: string) => {
    try {
      const res = await GetStoreCarouselListForStorePublic(id);

      const items = (res.data ?? [])
        .filter((item) => item.is_active)
        .sort((a, b) => a.ordering - b.ordering);
      setCarouselData(items);
    } catch (error) {
      console.log('Error fetching store carousels', error);
      setCarouselData([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoaded(false);
    fetchCarouselData(id);
  }, [id]);

  // Hold the banner's space while loading so nothing jumps, and so the real
  // banners don't briefly show the fallback first
  if (id && !loaded) {
    return (
      <div className="rounded-4 bg-light" style={{ aspectRatio: '4 / 1' }} />
    );
  }

  if (carouselData.length === 0) {
    // Same artwork as the app's modals, with optional content on top
    return (
      <div
        className="position-relative rounded-4 overflow-hidden"
        style={{
          aspectRatio: '4 / 1',
          backgroundImage: `url(${BrandBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {fallbackContent && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center">
            {fallbackContent}
          </div>
        )}
      </div>
    );
  }

  return <ImageCarousel items={getImageCarouselItems(carouselData)} />;
}
